/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import React from 'react';
import { LocalDate } from '@js-joda/core';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

// models and services
import { IncomeSourceModel } from 'src/types/income-source.model';
import { IncomeDrawerViewModel } from 'src/types/view-models/income-drawer.view.model';
import { IncomeDrawerFormValues } from 'src/types/income-drawer-form-values.model';
import LiteralsService from '../../services/literals/LiteralsService';
import NavigationService from '../../services/navigation.service';

// components
import Drawer from '../../components/drawer/Drawer';
import { ButtonPrimary, ButtonSecondary, IconOpenBanking } from '@americanexpress/dls-react';
import OptionsBox from '../../components/options-box/OptionsBox';
import BasicInput from 'src/components/formik-fields/basic-input/BasicInput';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';
import Dropdown from 'src/components/formik-fields/dropdown/Dropdown';
import DateInput from 'src/components/formik-fields/date-input/DateInput';

// styles
import './IncomeDrawer.scss';
import Selectors from 'src/utils/selectors';

export default function IncomeDrawer({
    drawerProps,
    user,
    handleAccounts,
    handleBack,
    handleClearFirstImport,
    handleDeleteIncome,
    handleReloadUser,
    handleFirstImport,
    handleSaveIncome,
    history,
    firstImport,
    setDrawer
}: IncomeDrawerViewModel): JSX.Element {
    const currentIncomeSource: IncomeSourceModel = drawerProps?.currentIncomeSource;
    if (!currentIncomeSource) {
        return null;
    }

    const handleDelete = async (incomeSource: IncomeSourceModel, values: IncomeDrawerFormValues): Promise<void> => {
        await handleDeleteIncome(Object.assign({}, currentIncomeSource, values));
        await handleReloadUser();
        await NavigationService.goBack(history);
    };

    const currentDateValue: LocalDate = currentIncomeSource.startDate || LocalDate.now().minusMonths(1);

    const buttonText: string = LiteralsService.getLiteral(
        currentIncomeSource.incomeUid ? 'IncomeDrawer.updateButton' : 'IncomeDrawer.addButton'
    );

    const validationSchema = Yup.object({
        name: Yup.string().required(LiteralsService.getLiteral('InvestmentDrawer.nameInputError')),
        annualizedIncome: Yup.number()
            .moreThan(0, LiteralsService.getLiteral('FormErrors.amountGreaterThanZero'))
            .typeError('Must be a number')
            .required(LiteralsService.getLiteral('FormErrors.amountRequired')),
        startDate: Yup.date()
            .required(LiteralsService.getLiteral('FormErrors.startDateRequired'))
            .min(LocalDate.now().minusYears(100), LiteralsService.getLiteral('FormErrors.startDateRequired'))
            .max(LocalDate.now(), LiteralsService.getLiteral('FormErrors.startDatePast')),
        frequency: Yup.mixed()
            .oneOf(['month', 'year'], 'Invalid value')
            .required(LiteralsService.getLiteral('FormErrors.frequencyRequired'))
    });

    return (
        <Drawer
            drawerProps={drawerProps}
            handleBack={handleBack}
            title={LiteralsService.getLiteral(
                currentIncomeSource.incomeUid ? 'IncomeDrawer.editTitle' : 'IncomeDrawer.addTitle'
            )}
            withBackButton
            className="income-drawer"
        >
            <OptionsBox
                headerText={LiteralsService.getLiteral('IncomeDrawer.ConnectAccount.header')}
                icon={<IconOpenBanking />}
                content={[
                    LiteralsService.getLiteral('IncomeDrawer.ConnectAccount.content1'),
                    LiteralsService.getLiteral('IncomeDrawer.ConnectAccount.content2', [LiteralsService.getAppName()])
                ]}
                isBlue
                hasPlaidButton
                handleSuccess={async () => await NavigationService.goBack(history)}
                firstImport={firstImport}
                handleAccounts={handleAccounts}
                handleClearFirstImport={handleClearFirstImport}
                handleFirstImport={handleFirstImport}
                setDrawer={setDrawer}
                user={user}
            />
            <Formik
                initialValues={{
                    name: currentIncomeSource.name,
                    annualizedIncome: currentIncomeSource.annualizedIncome || undefined,
                    startDate: currentDateValue,
                    frequency: currentIncomeSource.frequency || 'year'
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    handleSaveIncome(user, Object.assign({}, currentIncomeSource, values));
                    handleBack();
                }}
            >
                {({ values, handleSubmit }) => (
                    <Form>
                        {' '}
                        <BasicInput id="name" label={LiteralsService.getLiteral('IncomeDrawer.nameInputLabel')} />
                        <CurrencyInput
                            id="annualizedIncome"
                            label={LiteralsService.getLiteral('IncomeDrawer.amountInputLabel')}
                            hint={LiteralsService.getLiteral('IncomeDrawer.amountInputHint')}
                        />
                        <DateInput
                            label={LiteralsService.getLiteral('IncomeDrawer.startDateInputLabel')}
                            id="startDate"
                            data-cy="startDateInput"
                        />
                        <Dropdown id="frequency" label={LiteralsService.getLiteral('IncomeDrawer.frequencyInputLabel')}>
                            <option key="Monthly" value="month">
                                Monthly
                            </option>
                            <option key="Annually" value="year">
                                Annually
                            </option>
                        </Dropdown>
                        <div className="bottomButtons" data-cy={Selectors.PlanAhead.DELETE_BUTTON}>
                            <ButtonPrimary data-cy={Selectors.General.UPDATE_BUTTON} onClick={() => handleSubmit()}>
                                {buttonText}
                            </ButtonPrimary>
                            {currentIncomeSource.incomeUid && (
                                <ButtonSecondary onClick={async () => await handleDelete(currentIncomeSource, values)}>
                                    {LiteralsService.getLiteral('CommonText.delete')}
                                </ButtonSecondary>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </Drawer>
    );
}
